/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import {
  adSpendEndDate,
  formatBrlFromCents,
  summaryTotalCents,
  totalSpendCents,
} from '@/shared/utils/ad-dialog-helpers';
import { AdDialogShell } from './AdDialogShell';
import { MetaAdsTermsLinks } from './MetaAdsTermsLinks';
import type { AdSpendItem, AdSpendSummary } from './ad-dialog-types';

const ITEM_LABEL_KEYS: Record<AdSpendItem['label'], string> = {
  facebookLeadAd: 'adDialogs.spendConfirm.itemFacebookLeadAd',
  instagramLeadAd: 'adDialogs.spendConfirm.itemInstagramLeadAd',
  boost: 'adDialogs.spendConfirm.itemBoost',
};

/**
 * Asks the user to confirm a paid Meta action before it is created. It states,
 * for each ad, the daily budget, how long it runs and when it ends, and the
 * most it can cost in total, and says that Meta -- not Housien -- charges the
 * payment method on their ad account. Confirming is the user's authorization
 * for exactly those amounts; the request carries it and the server records it.
 * There is deliberately no "don't ask again": every spend is confirmed.
 */
export function AdSpendConfirmModal({
  summary,
  onConfirm,
  onCancel,
}: {
  summary: AdSpendSummary;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation('features/properties');
  const start = summary.startsAt ?? new Date();
  const formatDate = (date: Date) => date.toLocaleDateString(i18n.language);

  return (
    <AdDialogShell
      titleId="ad-spend-confirm-title"
      title={t('adDialogs.spendConfirm.title')}
      onDismiss={onCancel}
      testId="ad-spend-confirm-modal"
    >
      <div className="flex flex-col gap-(--space-2)">
        {summary.startsAt && (
          <Text
            variant="bodySm"
            tone="primary"
            className="m-0 leading-[var(--leading-normal)]"
            data-testid="ad-spend-confirm-starts-at"
          >
            {t('adDialogs.spendConfirm.startsAt', { start: formatDate(summary.startsAt) })}
          </Text>
        )}
        {summary.items.map((item) => (
          <Text
            key={item.label}
            variant="bodySm"
            tone="primary"
            className="m-0 leading-[var(--leading-normal)]"
            data-testid={`ad-spend-confirm-item-${item.label}`}
          >
            {t('adDialogs.spendConfirm.itemLine', {
              label: t(ITEM_LABEL_KEYS[item.label]),
              daily: formatBrlFromCents(item.dailyBudgetCents),
              days: item.durationDays,
              end: formatDate(adSpendEndDate(start, item.durationDays)),
              total: formatBrlFromCents(totalSpendCents(item.dailyBudgetCents, item.durationDays)),
            })}
          </Text>
        ))}
        <Text
          variant="bodySm"
          tone="primary"
          fw={600}
          className="m-0 leading-[var(--leading-normal)]"
          data-testid="ad-spend-confirm-total"
        >
          {t('adDialogs.spendConfirm.total', { total: formatBrlFromCents(summaryTotalCents(summary)) })}
        </Text>
        <Text variant="small" tone="secondary" className="m-0 leading-[var(--leading-normal)]">{t('adDialogs.spendConfirm.chargeNotice')}</Text>
        <Text variant="small" tone="secondary" className="m-0 leading-[var(--leading-normal)]" data-testid="ad-spend-confirm-authorization">
          {t('adDialogs.spendConfirm.authorization')}
        </Text>
        <MetaAdsTermsLinks />
      </div>
      <div className="flex flex-wrap justify-end gap-(--space-2) pt-(--space-1)">
        <Button type="button" variant="outlined" size="sm" onClick={onCancel} data-testid="ad-spend-confirm-cancel">
          {t('adDialogs.spendConfirm.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={onConfirm} data-testid="ad-spend-confirm-accept">
          {summary.kind === 'boost' ? t('adDialogs.spendConfirm.confirmBoost') : t('adDialogs.spendConfirm.confirmLeadAd')}
        </Button>
      </div>
    </AdDialogShell>
  );
}
